<script setup>
import { AppModelsProgrammingSessionCancellationPolicyType as CancellationPolicyType } from "@classes-service-types/app-models-programming-session-cancellation-policy-type.ts";
import { useVuelidate } from "@vuelidate/core";
import { useVModel } from "@vueuse/core";
import { format, parse } from "date-fns";
import { computed, onBeforeMount, ref, unref, watch } from "vue";
import WYSIWYG from "@/components/forms/SharedComponents/WYSIWYG/WYSIWYG.vue";
import {
  acceptanceDecisions,
  gpaExceptionRequirementsOptions,
  learnerTypesOptions,
  minYearInSchoolOptions,
  proficiencyLevelsOptions,
  REGISTRATION_TYPE,
  requiredLanguagesOptions,
} from "@/components/program-manager/sessions/constants";
import {
  buildCancellationPolicyOptions,
  checkIfProgramSessionTypeIsInternshipOnly,
  resolveDefaultCancellationPolicyType,
} from "@/components/program-manager/sessions/utils.js";
import CalendarInput from "@/components/shared/CalendarInput.vue";
import { CheckboxCheckmark } from "@/components/shared/icons";
import BasicCheckbox from "@/components/shared/input/BasicCheckbox.vue";
import BasicTextInput from "@/components/shared/input/BasicTextInput.vue";
import RadioYesNo from "@/components/shared/input/RadioYesNo.vue";
import ExpandingSection from "@/components/shared/section/ExpandingSection.vue";
import CheckboxSelect from "@/components/shared/select/CheckboxSelect.vue";
import VSelectCaret from "@/components/shared/select/VSelectCaret.vue";
import HelpIconPrimeVueTooltip from "@/components/shared/Tooltip/HelpIconPrimeVueTooltip.vue";
import { hasConfigurableCancellationPolicyEnabled, useCustomPriceFees } from "@/composables/authorization.js";
import { DEFAULT_APPLICATION_FEES } from "@/constants";
import { getEntityById, toTwoDecimals, updateFieldToDecimals } from "@/services/utils";
import ApplicationRequirementsFeesPayments from "./ApplicationRequirementsFeesPayments/index.vue";

// Vue props
const props = defineProps({
  modelValue: {
    type: Object,
    default: () => {},
  },
  rules: {
    type: Object,
    default: () => {},
  },
  canEditConfigurableFees: {
    type: Boolean,
    default: true,
  },
  // The session's resolved owning entity id (the selected owner when tenant libraries are on),
  // used to drive cancellation policy options. Falls back to programSession.owner_entity_id.
  ownerEntityId: {
    type: String,
    default: undefined,
  },
});

const emit = defineEmits(["update:modelValue"]);
const programSession = useVModel(props, "modelValue", emit);

// Const
const externalLinkPlaceholder = "{External Link Site Name}";
const externalLinkUrlPlaceholder = "https://";

const ownerEntity = ref(undefined);

const ownerPolicyUrl = computed(() => ownerEntity.value?.cancellation_policy_url || undefined);
const cancellationPolicyOptions = computed(() => buildCancellationPolicyOptions(ownerEntity.value));

const isSessionSpecificCancellationPolicy = computed(
  () => programSession.value?.cancellation_policy_type === CancellationPolicyType.Session,
);

// Institutional policies resolve their URL from the entity record, so the section offers a link to
// the owner's policy rather than an input.
const isInstitutionalCancellationPolicy = computed(
  () =>
    programSession.value?.cancellation_policy_type === CancellationPolicyType.Api ||
    programSession.value?.cancellation_policy_type === CancellationPolicyType.Entity,
);

const tooltips = {
  registrationType: {
    heading: "Discover more about each registration type by clicking ‘Registration Type’",
    register: "Indicates session includes account creation, student info, review, and checkout steps.",
  },
  externalLink: {
    url: "The link provided to students once their application is completed.",
    siteName: "The name of the website that is shown to the student and links out externally.",
    instructions:
      "Remember to replace [External Link Site Name] with the actual name of the external site (e.g. 'Sample University') or edit the message to better fit your program's needs.",
  },
  externalListing: {
    cta: 'The text that is shown to the student on the CTA button which links out externally.  If blank, "View More Information" will show as the default.',
  },
  registrationFee: "Student fee for application processing, not a part of the total program session price",
  confirmationDeposit: "Student deposit applied to the program session price.",
  is_gpa_required:
    "Yes - If selected, all students applying must submit their GPA <br /> No - If selected, no students are required to submit their GPA",
};

const config = {
  dateFormat: "m/d/Y",
  parseDate: (dateStr) => {
    return parse(dateStr, "yyyy-MM-dd", new Date(), {});
  },
};

// Refs
const fees = ref({
  registration: DEFAULT_APPLICATION_FEES.REGISTRATION_FEE,
  confirmation: DEFAULT_APPLICATION_FEES.CONFIRMATION_DEPOSIT,
});

const hasRegistrationFee = ref(true);
const hasConfirmationDeposit = ref(true);

const registrationOptions = ref({
  register: true,
  personalize: programSession.value?.is_personalized ?? true,
  apply: programSession.value?.requires_application ?? true,
  externalLink:
    (programSession.value?.registration_type !== REGISTRATION_TYPE.externalListing &&
      programSession.value?.is_external_link_required) ??
    false,
  externalListing: programSession.value?.registration_type === REGISTRATION_TYPE.externalListing,
});

// The GPA, age/academic, and language sections are optional inputs that should be
// available for every registration combination. Validations (the ** markers) stay
// gated on registrationOptions.apply, so they remain optional everywhere except Apply.
const displayAdditionalFields = computed(
  () =>
    registrationOptions.value.register || registrationOptions.value.apply || registrationOptions.value.externalListing,
);

const defaultExternalLinkInstructions = {
  content: [
    {
      content: [
        {
          text: "Get a head start with your onboarding materials by clicking 'Continue to Onboarding' below.",
          type: "text",
        },
        {
          type: "paragraph",
        },
        {
          content: [
            {
              text: "Please also visit ",
              type: "text",
            },
            {
              marks: [{ type: "bold" }],
              text: externalLinkPlaceholder,
              type: "text",
            },
            {
              text: "'s site to review important program details and other possible onboarding materials.",
              type: "text",
            },
          ],
          type: "paragraph",
        },
      ],
      type: "paragraph",
    },
  ],
  type: "doc",
};
const externalLinkInstructions = ref(
  programSession.value.external_link_instructions
    ? programSession.value.external_link_instructions
    : defaultExternalLinkInstructions,
);

watch(
  () => programSession.value.external_link_instructions,
  (newVal) => {
    if (!programSession.value.is_external_link_required && (newVal === null || newVal === undefined)) {
      externalLinkInstructions.value = defaultExternalLinkInstructions;
    }
  },
);

const minimum_age = computed(() => programSession.value.minimum_age ?? null);
const maximum_age = computed(() => programSession.value.maximum_age ?? null);

// Computed
const isGpaExceptionsAllowed = computed(() => !!programSession.value?.is_gpa_required ?? true);

const displayApplicationInfo = computed(
  () => registrationOptions.value.apply || registrationOptions.value.externalListing,
);

const expandedGPA = computed(() => registrationOptions.value.apply);
const expandedAgeRestrictions = computed(
  () =>
    (!!minimum_age.value && minimum_age.value > 0) ||
    (!!maximum_age.value && minimum_age.value > 0) ||
    !!programSession.value.education_levels ||
    !!programSession.value.academic_level_id,
);
const expandedAdmissions = computed(
  () =>
    programSession.value.acceptance_decisions?.length > 0 ||
    (!!programSession.value.difficulty_score && programSession.value.difficulty_score > 0) ||
    !!programSession.value.prerequisites ||
    !!programSession.value.corequisities,
);
const expandedLanguage = computed(() => programSession.value.required_languages.length > 0);

const hasProgramSessionTypeInternshipOnly = computed(() =>
  checkIfProgramSessionTypeIsInternshipOnly(unref(programSession)),
);

// Function
const updateRegistrationTypeOptions = (option = "", isSelected = false) => {
  switch (option) {
    case "personalize":
      registrationOptions.value.register = true;
      registrationOptions.value.externalListing = false;
      break;
    case "apply":
      registrationOptions.value.apply = isSelected;
      if (isSelected) {
        registrationOptions.value.register = true;
        registrationOptions.value.externalListing = false;
      }
      break;
    case "external-link":
      registrationOptions.value.register = true;
      registrationOptions.value.externalListing = false;
      programSession.value.is_external_link_required = isSelected;
      break;
    case "external-listing":
      registrationOptions.value.register = !isSelected;
      programSession.value.is_external_link_required = isSelected;
      if (isSelected) {
        registrationOptions.value.personalize = false;
        registrationOptions.value.apply = false;
        registrationOptions.value.externalLink = false;
      }
      break;
  }
  programSession.value.is_personalized = registrationOptions.value.personalize;
  programSession.value.requires_application = registrationOptions.value.apply;
  programSession.value.registration_type = registrationOptions.value.register
    ? REGISTRATION_TYPE.register
    : REGISTRATION_TYPE.externalListing;
};

const resolvedOwnerEntityId = computed(() => props.ownerEntityId ?? programSession.value?.owner_entity_id);

const loadOwnerCancellationPolicy = async () => {
  const ownerEntityId = resolvedOwnerEntityId.value;
  ownerEntity.value = ownerEntityId ? await getEntityById(ownerEntityId) : undefined;
  programSession.value.cancellation_policy_type = resolveDefaultCancellationPolicyType(
    programSession.value?.cancellation_policy_type,
    cancellationPolicyOptions.value,
  );
};

const onCancellationPolicyTypeChange = () => {
  // Only session-specific policies carry a URL on the session; institutional ones resolve from the entity.
  if (!isSessionSpecificCancellationPolicy.value) {
    programSession.value.cancellation_policy_url = undefined;
  }
};

const replacePlaceholderText = (contents = [], replaceValue = "") => {
  if (contents && contents.length > 0) {
    const textMatches = contents.filter((contentItem) => Object.keys(contentItem).some((key) => key === "text"));
    for (const textItem of textMatches) {
      textItem["text"] = textItem["text"].replace(externalLinkPlaceholder, replaceValue);
    }
    const moreContents = contents.filter((contentItem) => Object.keys(contentItem).some((key) => key === "content"));
    moreContents.forEach((contentItem) => replacePlaceholderText(contentItem["content"], replaceValue));
  }
};

const updateExternalLinkInstructionsWithSiteName = (linkSiteName) => {
  const instructions = externalLinkInstructions.value;
  if (instructions?.content) {
    replacePlaceholderText(instructions.content, linkSiteName);
  }
  externalLinkInstructions.value = { ...instructions };
};

const updateExternalLinkInstructions = (newValue) => {
  programSession.value.external_link_instructions = JSON.stringify(newValue);
};

const normalizeExternalLinkUrl = (value) => (value == null ? "" : `${value}`.trim());

const handleExternalLinkUrlFocus = () => {
  if (!normalizeExternalLinkUrl(programSession.value.external_link_url)) {
    programSession.value.external_link_url = externalLinkUrlPlaceholder;
  }
};

const handleExternalLinkUrlBlur = (event) => {
  const normalizedValue = normalizeExternalLinkUrl(event?.target?.value ?? programSession.value.external_link_url);

  programSession.value.external_link_url =
    normalizedValue && normalizedValue !== externalLinkUrlPlaceholder ? normalizedValue : undefined;

  v$.external_link_url?.$touch?.();
};

const updateMinimumAge = (newValue) => {
  programSession.value.minimum_age = newValue.length ? newValue : null;
};

const updateMaximumAge = (newValue) => {
  programSession.value.maximum_age = newValue.length ? newValue : null;
};

const numberConvert = (event) => {
  if (isNaN(event.key)) {
    event.preventDefault();
  }
};

let v$ = {};
onBeforeMount(() => {
  v$ = useVuelidate(props.rules, programSession, {
    $registerAs: "ApplicationSection",
    $lazy: true,
  });
  if (hasConfigurableCancellationPolicyEnabled.value) {
    loadOwnerCancellationPolicy();
  }
});

// Re-pre-populate the policy when the owning institution changes. Gated so an off flag never mutates the session.
watch(resolvedOwnerEntityId, () => {
  if (hasConfigurableCancellationPolicyEnabled.value) {
    loadOwnerCancellationPolicy();
  }
});
</script>
<template>
  <div class="grid grid-cols-1 gap-y-4">
    <div class="flex align-items">
      <div class="flex self-center">
        <a
          href="https://api-internal.apiabroad.com/hc/en-us/articles/32537472952087-Configurable-Registration-Types"
          target="_blank"
          rel="noreferrer"
        >
          <span class="text-sm text-university-secondary font-semibold">Registration Type</span>
        </a>
        <HelpIconPrimeVueTooltip :message="tooltips.registrationType.heading" custom-style="self-center p-1" />
      </div>
    </div>
    <div class="grid grid-cols-2 gap-x-24 gap-y-4">
      <div class="flex align-items">
        <div class="self-center h-4 w-4">
          <checkbox-checkmark v-if="registrationOptions.register" rgb-fill-color="rgb(30, 38, 76)" />
        </div>
        <label
          :class="['text-sm font-semibold ml-3', registrationOptions.register ? 'text-gray-700' : 'text-gray-500']"
        >
          Register
        </label>
        <div class="flex self-center ml-1">
          <HelpIconPrimeVueTooltip :message="tooltips.registrationType.register" />
        </div>
      </div>
      <basic-checkbox
        v-model="registrationOptions.externalLink"
        data-testid="standard-application-registration-options-external-link-checkbox"
        class="my-auto"
        field-label="External Link"
        :disabled="registrationOptions.externalListing"
        :should-disabled-text="true"
        @update:model-value="(val) => updateRegistrationTypeOptions('external-link', val)"
      />
      <basic-checkbox
        v-model="registrationOptions.personalize"
        data-testid="standard-application-registration-options-personalize-checkbox"
        class="my-auto"
        field-label="Personalize"
        :disabled="registrationOptions.externalListing"
        :should-disabled-text="true"
        @update:model-value="(val) => updateRegistrationTypeOptions('personalize', val)"
      />
      <basic-checkbox
        v-model="registrationOptions.externalListing"
        data-testid="standard-application-registration-options-external-listing-checkbox"
        class="my-auto"
        field-label="External Listing"
        :disabled="registrationOptions.apply || registrationOptions.personalize || registrationOptions.externalLink"
        :should-disabled-text="true"
        @update:model-value="(val) => updateRegistrationTypeOptions('external-listing', val)"
      />
      <basic-checkbox
        v-model="registrationOptions.apply"
        data-testid="standard-application-registration-options-apply-checkbox"
        class="my-auto col-span-2"
        field-label="Apply"
        :disabled="registrationOptions.externalListing"
        :should-disabled-text="true"
        @update:model-value="(val) => updateRegistrationTypeOptions('apply', val)"
      />
      <basic-text-input
        v-if="registrationOptions.externalLink"
        v-model="programSession.external_link_url"
        data-testid="standard-application-registration-options-external-link-url-text-input"
        input-classes="h-10 w-full form-input"
        outer-classes="col-span-2"
        :maxlength="255"
        :placeholder="externalLinkUrlPlaceholder"
        :vuelidate-instance="v$.external_link_url"
        @focus="handleExternalLinkUrlFocus"
        @blur="handleExternalLinkUrlBlur"
      >
        <template #fieldLabel>
          <div class="flex align-items">
            <label class="common-label-text flex self-center">
              <span class="text-red-100 pr-2">**</span>External Link URL
            </label>
            <div class="flex self-center ml-1">
              <HelpIconPrimeVueTooltip :message="tooltips.externalLink.url" />
            </div>
          </div>
        </template>
      </basic-text-input>
      <basic-text-input
        v-if="registrationOptions.externalLink"
        v-model="programSession.external_link_site_name"
        data-testid="standard-application-registration-options-external-link-site-name-text-input"
        outer-classes="col-span-2"
        input-classes="h-10 w-full form-input"
        :maxlength="30"
        :vuelidate-instance="v$.external_link_site_name"
        @blur="(e) => updateExternalLinkInstructionsWithSiteName(e.target.value)"
      >
        <template #fieldLabel>
          <div class="flex align-items">
            <label class="common-label-text flex self-center">
              <span class="text-red-100 pr-2">**</span>External Link Site Name
            </label>
            <div class="flex self-center ml-1">
              <HelpIconPrimeVueTooltip :message="tooltips.externalLink.siteName" />
            </div>
          </div>
        </template>
      </basic-text-input>
      <div
        v-if="registrationOptions.externalLink"
        class="col-span-2"
        data-testid="standard-application-registration-options-external-link-instructions-wysiwyg"
      >
        <div class="flex align-items">
          <label class="common-label-text flex self-center">
            <span class="text-red-100 pr-2">**</span>External Link Instructions
          </label>
          <div class="flex self-center ml-1">
            <HelpIconPrimeVueTooltip :message="tooltips.externalLink.instructions" />
          </div>
        </div>
        <WYSIWYG
          v-model="externalLinkInstructions"
          :content="externalLinkInstructions"
          :editable="true"
          :character-limit="10000"
          @update:model-value="updateExternalLinkInstructions"
        />
      </div>
      <basic-text-input
        v-if="registrationOptions.externalListing"
        v-model="programSession.external_link_url"
        data-testid="standard-application-program-session-external-link-url-text-input"
        input-classes="h-10 w-full form-input"
        outer-classes="col-span-2"
        :placeholder="externalLinkUrlPlaceholder"
        :vuelidate-instance="v$.external_link_url"
        @focus="handleExternalLinkUrlFocus"
        @blur="handleExternalLinkUrlBlur"
      >
        <template #fieldLabel>
          <label class="common-label-text"><span class="text-red-100 pr-2">**</span>External Listing URL</label>
        </template>
      </basic-text-input>
      <basic-text-input
        v-if="registrationOptions.externalListing"
        v-model="programSession.external_link_site_name"
        data-testid="standard-application-program-session-external-link-site-name-text-input"
        outer-classes="col-span-2"
        input-classes="h-10 w-full form-input"
        :maxlength="30"
        :vuelidate-instance="v$.external_link_site_name"
        @blur="(e) => updateExternalLinkInstructionsWithSiteName(e.target.value)"
      >
        <template #fieldLabel>
          <label class="common-label-text"
            >Custom Text for CTA Button
            <HelpIconPrimeVueTooltip :message="tooltips.externalListing.cta" custom-style="inline-block"
          /></label>
        </template>
      </basic-text-input>
      <calendar-input
        placeholder="Select date"
        name="date"
        data-testid="standard-application-program-session-registration-deadline-calendar-input"
        :config="config"
        :vuelidate-instance="v$.application_deadline"
        :model-value="programSession.application_deadline || null"
        @on-change="
          (date) =>
            (programSession.application_deadline = date && date[0] ? format(new Date(date[0]), 'yyyy-MM-dd') : '')
        "
        ><template #fieldLabel>
          <label class="common-label-text">
            <span class="text-red-100 pr-2">**</span>
            <span v-if="registrationOptions.apply">Application Deadline</span>
            <span v-else>Registration Deadline</span>
          </label>
        </template>
      </calendar-input>
    </div>
    <template v-if="hasConfigurableCancellationPolicyEnabled">
      <v-select-caret
        v-model="programSession.cancellation_policy_type"
        :options="cancellationPolicyOptions"
        :reduce="(val) => val.id"
        label="label"
        :vuelidate-instance="v$.cancellation_policy_type"
        :clearable="false"
        data-testid="program-session-cancellation-policy-dropdown"
        @update:modelValue="onCancellationPolicyTypeChange"
      >
        <template #fieldLabel>
          <label class="w-full common-label-text mt-4"
            ><span class="text-red-100 pr-2">**</span>Cancellation Policy Type</label
          >
        </template>
      </v-select-caret>
      <basic-text-input
        v-if="isSessionSpecificCancellationPolicy"
        v-model="programSession.cancellation_policy_url"
        input-classes="h-10 w-full form-input"
        :vuelidate-instance="v$.cancellation_policy_url"
        data-testid="program-session-cancellation-policy-url-input"
      >
        <template #fieldLabel>
          <label class="w-full common-label-text mt-4"
            ><span class="text-red-100 pr-2">**</span>Session-Specific Cancellation Policy URL</label
          >
        </template>
      </basic-text-input>
      <div v-else-if="isInstitutionalCancellationPolicy && ownerPolicyUrl">
        <a
          :href="ownerPolicyUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="text-sm text-university-secondary font-semibold hover:underline"
          >View Cancellation Policy</a
        >
      </div>
    </template>
    <ApplicationRequirementsFeesPayments
      v-if="useCustomPriceFees"
      v-model="programSession"
      :can-edit-fees="canEditConfigurableFees"
    />
    <expanding-section
      v-if="displayAdditionalFields"
      :expanded-by-default="expandedGPA"
      field-label="GPA"
      base-classes="border-t border-gray-300 pt-2"
    >
      <template #content>
        <div class="grid grid-cols-1 gap-x-24 gap-y-4">
          <div class="w-96">
            <radio-yes-no
              v-model="programSession.is_gpa_required"
              data-testid="standard-application-program-session-gpa-exceptions-allowed-radio"
              input-classes="h-10 w-full form-input"
              type="number"
              :vuelidate-instance="v$.is_gpa_required"
            >
              <template #fieldLabel>
                <div class="flex align-items">
                  <label name="is_gpa_required" class="common-label-text">
                    <span v-if="registrationOptions.apply" class="text-red-100 pr-2">**</span>Is a GPA required for
                    this session?
                  </label>
                  <div class="flex self-center ml-1">
                    <HelpIconPrimeVueTooltip :message="tooltips.is_gpa_required" />
                  </div>
                </div>
              </template>
            </radio-yes-no>
          </div>
          <div v-show="isGpaExceptionsAllowed" class="grid grid-cols-2 gap-x-24 gap-y-4">
            <basic-text-input
              v-model="programSession.minimum_gpa_requirement"
              data-testid="standard-application-program-session-minimum-gpa-requirement-text-input"
              input-classes="h-10 w-full form-input"
              :vuelidate-instance="v$.minimum_gpa_requirement"
              type="number"
              min="0"
              step=".01"
              @wheel.prevent
              @blur="() => updateFieldToDecimals('minimum_gpa_requirement', programSession)"
            >
              <template #fieldLabel>
                <label class="common-label-text"
                  ><span
                    v-if="!hasProgramSessionTypeInternshipOnly && registrationOptions.apply"
                    class="text-red-100 pr-2"
                    >**</span
                  >Minimum GPA Required</label
                >
              </template>
            </basic-text-input>
            <radio-yes-no
              v-model="programSession.gpa_exceptions_allowed"
              data-testid="standard-application-program-session-gpa-exceptions-allowed-radio"
              input-classes="h-10 w-full form-input"
              :vuelidate-instance="v$.gpa_exceptions_allowed"
              type="number"
            >
              <template #fieldLabel>
                <div class="flex align-items">
                  <label name="gpa_exceptions_allowed" class="common-label-text">
                    <span v-if="registrationOptions.apply" class="text-red-100 pr-2">**</span>GPA Exceptions Allowed?
                  </label>
                </div>
              </template>
            </radio-yes-no>
            <CheckboxSelect
              v-if="programSession.gpa_exceptions_allowed"
              v-model="programSession.gpa_exception_requirements"
              data-testid="standard-application-program-session-gpa-exceptions-requirements-checkbox"
              :options="gpaExceptionRequirementsOptions"
              label="name"
              :vuelidate-instance="v$.gpa_exception_requirements"
              :multiple="true"
              :deselect-from-dropdown="true"
              :close-on-select="false"
              placeholder="Pick a value"
            >
              <template #fieldLabel>
                <div class="flex align-items">
                  <label name="gpa_exception_requirements" class="common-label-text">
                    <span v-if="registrationOptions.apply" class="text-red-100 pr-2">**</span>GPA Exception
                    Requirements
                  </label>
                </div>
              </template>
            </CheckboxSelect>
          </div>
        </div>
      </template>
    </expanding-section>
    <expanding-section
      v-if="displayAdditionalFields"
      :expanded-by-default="expandedAgeRestrictions"
      field-label="AGE/ACADEMIC YEAR RESTRICTIONS"
      base-classes="border-t border-gray-300 pt-2"
    >
      <template #content>
        <div class="grid grid-cols-2 gap-x-24 gap-y-4">
          <CheckboxSelect
            v-model="programSession.education_levels"
            data-testid="standard-application-program-session-education-levels-checkbox"
            :vuelidate-instance="v$.education_levels"
            :options="learnerTypesOptions"
            field-label="Open to"
            :multiple="true"
            :deselect-from-dropdown="true"
            :close-on-select="false"
            placeholder="Pick a value"
            label="value"
          />
          <v-select-caret
            v-model="programSession.academic_level_id"
            data-testid="standard-application-program-session-academic-level-id-select"
            :vuelidate-instance="v$.academic_level_id"
            :options="minYearInSchoolOptions"
            :reduce="(val) => val.id"
            label="academic_year"
            field-label="Minimum Year in School"
          />
          <basic-text-input
            v-model="minimum_age"
            data-testid="standard-application-program-session-minimum-age-text-input"
            input-classes="h-10 w-full form-input"
            :vuelidate-instance="v$.minimum_age"
            type="number"
            min="0"
            max="100"
            @update:model-value="updateMinimumAge"
            @wheel.prevent
            @keypress="numberConvert"
          >
            <template #fieldLabel>
              <span class="common-label-text mr-2">Minimum Age</span>
            </template>
          </basic-text-input>
          <basic-text-input
            v-model="maximum_age"
            data-testid="standard-application-program-session-maximum-age-text-input"
            input-classes="h-10 w-full form-input"
            :vuelidate-instance="v$.maximum_age"
            type="number"
            min="0"
            max="200"
            @update:model-value="updateMaximumAge"
            @wheel.prevent
            @keypress="numberConvert"
          >
            <template #fieldLabel>
              <span class="common-label-text mr-2">Maximum Age</span>
            </template>
          </basic-text-input>
        </div>
      </template>
    </expanding-section>
    <expanding-section
      v-if="displayApplicationInfo"
      :expanded-by-default="expandedAdmissions"
      field-label="ADMISSIONS"
      base-classes="border-t border-gray-300 pt-2"
    >
      <template #content>
        <div class="grid grid-cols-2 gap-x-24 gap-y-4">
          <checkbox-select
            v-model="programSession.acceptance_decisions"
            :vuelidate-instance="v$.acceptance_decisions"
            :options="acceptanceDecisions"
            :multiple="true"
            :deselect-from-dropdown="true"
            :close-on-select="false"
            data-testid="program-session-acceptance-decisions-checkbox"
            placeholder="Pick a value"
            label="name"
            field-label="Admission Decision"
          />
          <basic-text-input
            v-model="programSession.difficulty_score"
            data-testid="standard-application-program-difficulty-score-text-input"
            input-classes="h-10 w-full form-input"
            :vuelidate-instance="v$.difficulty_score"
            type="number"
            min="0"
            step=".01"
            field-label="Acceptance Difficulty"
            @wheel.prevent
            @blur="() => (programSession.difficulty_score = toTwoDecimals(programSession.difficulty_score))"
          />
          <basic-text-input
            v-model="programSession.prerequisites"
            outer-classes="col-span-2"
            input-classes="h-10 w-full form-input"
            :vuelidate-instance="v$.prerequisites"
            field-label="Required for Acceptance"
          />
          <basic-text-input
            v-model="programSession.corequisities"
            outer-classes="col-span-2"
            input-classes="h-10 w-full form-input"
            :vuelidate-instance="v$.corequisities"
            field-label="Co-requisites"
          />
        </div>
      </template>
    </expanding-section>
    <expanding-section
      v-if="displayAdditionalFields"
      :expanded-by-default="expandedLanguage"
      field-label="LANGUAGE"
      base-classes="border-t border-gray-300 pt-2"
    >
      <template #content>
        <div class="grid grid-cols-2 gap-x-24 gap-y-4">
          <checkbox-select
            v-model="programSession.required_languages"
            :vuelidate-instance="v$.required_languages"
            :options="requiredLanguagesOptions"
            :multiple="true"
            :deselect-from-dropdown="true"
            :close-on-select="false"
            data-testid="standard-application-program-difficulty-score-text-input"
            placeholder="Pick a value"
            label="value"
            field-label="Language Required"
          />
          <v-select-caret
            v-model="programSession.min_proficiency_level_id"
            :vuelidate-instance="v$.min_proficiency_level_id"
            :options="proficiencyLevelsOptions"
            :reduce="(val) => val.id"
            data-testid="standard-application-program-min-proficiency-level-id-select"
            field-label="Language Level Required"
            label="value"
          />
        </div>
      </template>
    </expanding-section>
  </div>
</template>
